// Comprehensive list of Manitoba cities/towns served by Manitoba Security Services.
// Every entry includes SEO-focused, unique long-form copy. Larger cities get bespoke
// content; smaller communities interweave region-specific detail (economy, geography,
// service intersection) with keyword-rich descriptors.

export const cities = [
  {
    slug: "winnipeg",
    name: "Winnipeg",
    region: "Capital Region",
    tier: "primary",
    lat: 49.8951,
    lng: -97.1384,
    keywords: [
      "security guard services Winnipeg",
      "mobile patrol Winnipeg",
      "event security Winnipeg",
      "corporate security Winnipeg",
      "retail loss prevention Winnipeg",
      "construction site security Winnipeg",
      "condo concierge Winnipeg",
    ],
    intro:
      "Winnipeg is the operational spine of Manitoba Security Services. From the downtown financial core along Portage and Main, through the Exchange District's hospitality corridor, out into the Inkster, Symington, and CentrePort industrial belts, and across the residential neighbourhoods of River Heights, Tuxedo, Bridgwater, Sage Creek, St. Vital, St. Boniface, and Charleswood, our teams provide continuous coverage across every category of security service.",
    sections: [
      {
        h: "A city with genuine security complexity",
        p: "With a population approaching 850,000, Winnipeg carries the full spectrum of urban security demand: high-rise condominium concierge, corporate lobby posture in downtown office towers, retail loss prevention across malls including Polo Park, St. Vital Centre, Kildonan Place, Grant Park, and Garden City Shopping Centre, event security at the RBC Convention Centre and Canada Life Centre, construction-site protection on projects from downtown mixed-use developments to greenfield builds along the perimeter, and mobile patrol across industrial parks that never fully sleep. Manitoba Security Services staffs each category with officers trained specifically for the environment — the disciplines are related but not interchangeable.",
      },
      {
        h: "Coverage across every quadrant",
        p: "Our marked mobile patrol fleet covers every quadrant of Winnipeg — north through Garden City, West Kildonan, and Amber Trails; south through Fort Garry, Whyte Ridge, and Waverley West; east across Transcona, St. Boniface, and Windsor Park; west through St. James, Silver Heights, and Charleswood — plus the Perimeter Highway ring that connects them. Officers dispatched from strategically positioned patrol units respond to alarm activations, conduct randomised checks, and support opening and closing procedures for commercial clients on tight schedules.",
      },
      {
        h: "Industry-specific expertise",
        p: "Winnipeg's economy is diversified — finance and insurance, transportation and logistics, manufacturing, agri-food, biotech, aerospace, healthcare, and a growing technology sector. Each carries its own security profile. Financial-sector clients on Portage and Main need concierge-grade lobby posture and executive protection interface. CentrePort Canada tenants need cargo-integrity officers and gate control. Manitoba's aerospace corridor requires officers cross-trained in controlled-goods handling. Manitoba Security Services matches officer profile to sector need — not the other way around.",
      },
      {
        h: "Local coverage — neighbourhoods, venues, and response time",
        p: "Our Winnipeg coverage is street-level and neighbourhood-specific. Teams work daily across Downtown Winnipeg, the Exchange District, Polo Park, St. Boniface, Fort Garry, Inkster Industrial Park, and the Winnipeg Airport area — with the same officers returning to the same properties so they know the tenants, the delivery schedules, and the normal rhythm of the site. We protect hospitals and healthcare facilities, retail stores and shopping centres, office buildings, warehouses, industrial facilities, construction sites, distribution centres, apartment buildings, parking lots, and commercial properties of every size. Mobile patrol units run marked routes through every quadrant of the city around the clock, which means alarm activations, lock checks, and incident response are handled by an officer who is already nearby. For clients with multiple sites — a retail portfolio, a property-management group, a builder with concurrent projects — a single account manager coordinates every posting under one standard of documentation and reporting. And because our patrol fleet is already on the road across the city, response time is 45 minutes. That standard holds downtown at noon and in the industrial parks at three in the morning.",
      },
    ],
  },
  {
    slug: "brandon",
    name: "Brandon",
    region: "Westman",
    tier: "primary",
    lat: 49.8483,
    lng: -99.9501,
    keywords: [
      "security guard services Brandon",
      "mobile patrol Brandon",
      "event security Brandon",
      "corporate security Brandon",
      "construction security Brandon",
      "retail security Brandon",
    ],
    intro:
      "Brandon is Manitoba's second-largest city and the economic centre of Westman. Manitoba Security Services maintains active coverage across the downtown core, the Brandon industrial belt, and the surrounding agricultural service communities.",
    sections: [
      {
        h: "Westman's economic anchor",
        p: "Brandon anchors an agricultural, manufacturing, and logistics economy that stretches from Virden and Souris in the west, through Melita and Boissevain in the south, up to Minnedosa, Neepawa, and Russell. Our Brandon-based mobile patrol serves this radius, providing continuous coverage for commercial clients, agricultural facilities, event venues, and construction projects.",
      },
      {
        h: "Event security across the Keystone Centre and beyond",
        p: "The Keystone Centre — home to the Royal Manitoba Winter Fair and the Manitoba Livestock Expo — draws regional and national attendance. Manitoba Security Services deploys event-trained officers for these fixtures, plus concert engagements, corporate events, and private functions across Brandon.",
      },
      {
        h: "Industrial and construction site coverage",
        p: "Brandon's manufacturing base — food processing, plastics, agri-services — combined with active residential and commercial construction, generates steady demand for after-hours patrols, static coverage during high-value trade stack, and copper-theft prevention across active job sites.",
      },
    ],
  },
  {
    slug: "steinbach",
    name: "Steinbach",
    region: "Southeast",
    tier: "primary",
    lat: 49.5257,
    lng: -96.6839,
    keywords: [
      "security guard services Steinbach",
      "mobile patrol Steinbach",
      "commercial security Steinbach",
      "construction security Steinbach",
      "event security Steinbach",
    ],
    intro:
      "Steinbach is one of Manitoba's fastest-growing communities and a major service centre for the southeast. Manitoba Security Services maintains structured mobile patrol and static coverage across the city's expanding commercial and industrial footprint.",
    sections: [
      {
        h: "A growth city with growing security demand",
        p: "Steinbach's rapid population growth, driven by manufacturing, agri-food, and cross-border trade, has produced a corresponding expansion in commercial, retail, and industrial real estate — all of which require reliable security coverage. Our teams cover big-box retail, industrial parks, and construction projects across the city and its surrounding communities including Ste. Anne, Blumenort, Mitchell, and Kleefeld.",
      },
      {
        h: "Community-fit officers",
        p: "Officers assigned to Steinbach postings understand the community and the tone of engagement expected. Presentation and communication standards are calibrated to the professional environment of the region.",
      },
    ],
  },
  {
    slug: "thompson",
    name: "Thompson",
    region: "Northern",
    tier: "primary",
    lat: 55.7435,
    lng: -97.8558,
    keywords: [
      "security guard services Thompson",
      "mining security Thompson",
      "remote site security Thompson",
      "industrial security Thompson",
      "mobile patrol Thompson",
    ],
    intro:
      "Thompson is northern Manitoba's largest community and the operational base for a significant portion of the province's nickel mining and downstream processing. Manitoba Security Services provides mining, industrial, and commercial coverage across the city and adjoining sites.",
    sections: [
      {
        h: "A resource-sector security economy",
        p: "Thompson's economy is dominated by the nickel operations that put the city on the map. Mine gates, plant perimeters, fuel storage, warehouses, and contractor lay-down yards all require the specific competencies of a mining-experienced security provider. Our officers are cross-trained for the site's requirements and understand the pace of a 24/7 industrial environment in sub-arctic conditions.",
      },
      {
        h: "Community coverage",
        p: "Beyond the resource sector, Thompson requires standard urban security services — retail loss prevention, event security, commercial property patrol, healthcare security at the Thompson General Hospital campus, and residential coverage for higher-density housing. Manitoba Security Services staffs each of these categories with officers who live in and around the community.",
      },
    ],
  },
  {
    slug: "portage-la-prairie",
    name: "Portage la Prairie",
    region: "Central Plains",
    tier: "primary",
    lat: 49.9728,
    lng: -98.2919,
    keywords: [
      "security guard services Portage la Prairie",
      "industrial security Portage la Prairie",
      "food processing security Portage",
      "mobile patrol Portage la Prairie",
    ],
    intro:
      "Portage la Prairie sits at the western edge of the Winnipeg commuter shed and anchors a substantial food-processing and agri-industrial cluster. Manitoba Security Services provides gate control, cargo integrity, and mobile patrol across the city's industrial parks and beyond.",
    sections: [
      {
        h: "Food, agri-processing, and logistics",
        p: "Portage's industrial base — potato processing, pea protein, dairy, feed and grain handling — requires officers who understand hygiene protocols and the operational demands of continuous processing environments. Our teams cover gate control, driver check-in, and after-hours patrol across the city's plant footprint.",
      },
      {
        h: "Commercial and event coverage",
        p: "Downtown Portage retail, the Southport commercial corridor, and community events — including the Portage Manitoba Sunflower Festival footprint — round out our coverage of the city.",
      },
    ],
  },
  {
    slug: "winkler",
    name: "Winkler",
    region: "Pembina Valley",
    tier: "primary",
    lat: 49.1815,
    lng: -97.9407,
    keywords: [
      "security guard services Winkler",
      "commercial security Winkler",
      "industrial security Winkler",
      "mobile patrol Winkler",
    ],
    intro:
      "Winkler is the retail and industrial hub of the Pembina Valley. Manitoba Security Services covers the city's expanding commercial footprint, manufacturing base, and residential developments.",
    sections: [
      {
        h: "A regional service centre",
        p: "Winkler's manufacturing and food-processing base has scaled steadily with the surrounding region's growth. Our teams provide mobile patrol, gate control, and event coverage across the city and its trade partners in Morden, Carman, and beyond.",
      },
    ],
  },
  {
    slug: "selkirk",
    name: "Selkirk",
    region: "Interlake",
    tier: "primary",
    lat: 50.1436,
    lng: -96.8839,
    keywords: [
      "security guard services Selkirk",
      "mobile patrol Selkirk",
      "commercial security Selkirk",
      "marina security Selkirk",
    ],
    intro:
      "Selkirk sits at the gateway between Winnipeg and the Interlake and Lake Winnipeg's south basin. Manitoba Security Services covers commercial, industrial, and seasonal security demand across the city and the surrounding beach communities.",
    sections: [
      {
        h: "Interlake gateway coverage",
        p: "Our Selkirk patrols extend to the beach communities of Winnipeg Beach, Gimli, Grand Marais, and Victoria Beach during the summer season, plus year-round coverage for Selkirk industrial and commercial clients.",
      },
    ],
  },
  {
    slug: "morden",
    name: "Morden",
    region: "Pembina Valley",
    tier: "primary",
    lat: 49.1917,
    lng: -98.1014,
    keywords: [
      "security guard services Morden",
      "event security Morden",
      "commercial security Morden",
      "mobile patrol Morden",
    ],
    intro:
      "Morden is a growing community with a strong retail and agri-processing base. Manitoba Security Services provides mobile patrol, event coverage — including the Morden Corn and Apple Festival footprint — and static coverage across the city.",
    sections: [
      {
        h: "Retail, event, and commercial coverage",
        p: "Our officers support downtown Morden retail, the Corn and Apple Festival, and industrial and commercial clients across the city and its trade partners in Winkler and Carman.",
      },
    ],
  },
  {
    slug: "dauphin",
    name: "Dauphin",
    region: "Parkland",
    tier: "primary",
    lat: 51.1494,
    lng: -100.05,
    keywords: [
      "security guard services Dauphin",
      "event security Dauphin",
      "mobile patrol Dauphin",
      "resource security Dauphin",
    ],
    intro:
      "Dauphin is the service centre of Manitoba's Parkland region and hosts one of the province's largest annual cultural festivals. Manitoba Security Services provides event, commercial, and mobile patrol coverage across the city.",
    sections: [
      {
        h: "Parkland regional coverage",
        p: "From Dauphin's Countryfest event footprint to year-round commercial and industrial clients, our teams cover the city and the surrounding Parkland communities including Roblin, Russell, Grandview, and Ste. Rose du Lac.",
      },
    ],
  },
  {
    slug: "the-pas",
    name: "The Pas",
    region: "Northern",
    tier: "primary",
    lat: 53.826,
    lng: -101.2543,
    keywords: [
      "security guard services The Pas",
      "industrial security The Pas",
      "mining security The Pas",
      "remote site security The Pas",
    ],
    intro:
      "The Pas anchors northwestern Manitoba's forestry and mining service economy. Manitoba Security Services provides industrial, mining, and commercial coverage across the town and surrounding First Nation communities.",
    sections: [
      {
        h: "Forestry, mining, and rail",
        p: "The town's economic base — forestry, mining logistics, and rail — requires officers who understand the pace and hazard profile of heavy industry. We staff gate control, mobile patrol, and remote site coverage with appropriately trained personnel.",
      },
    ],
  },
  {
    slug: "flin-flon",
    name: "Flin Flon",
    region: "Northern",
    tier: "primary",
    lat: 54.7681,
    lng: -101.8757,
    keywords: [
      "security guard services Flin Flon",
      "mining security Flin Flon",
      "industrial security Flin Flon",
      "remote site security Flin Flon",
    ],
    intro:
      "Flin Flon is a mining town in the truest sense — its history, its identity, and its economy are inseparable from the copper and zinc operations that have shaped the community for generations. Manitoba Security Services provides mining-focused security coverage across the city and adjoining sites.",
    sections: [
      {
        h: "Mining security is the specialty",
        p: "Our Flin Flon operation is built around the specific needs of mine site security — gate control, credential verification, contractor management, roving patrols, and emergency response coordination. Officers understand the operational language of the mine and the pace of a continuous industrial environment.",
      },
    ],
  },
  {
    slug: "neepawa",
    name: "Neepawa",
    region: "Westman",
    tier: "secondary",
    lat: 50.229,
    lng: -99.4661,
    keywords: [
      "security guard services Neepawa",
      "commercial security Neepawa",
      "industrial security Neepawa",
      "mobile patrol Neepawa",
    ],
    intro:
      "Neepawa is a growing agricultural service centre with a substantial food-processing footprint. Manitoba Security Services provides mobile patrol, gate control, and commercial coverage across the town.",
    sections: [
      {
        h: "Food processing and community coverage",
        p: "Our teams cover Neepawa's processing plants, downtown retail, and community event footprint. Officers are drawn from a Brandon-based dispatch that also serves Minnedosa, Gladstone, and Ste. Rose du Lac.",
      },
    ],
  },
  {
    slug: "beausejour",
    name: "Beausejour",
    region: "Eastman",
    tier: "secondary",
    lat: 50.058,
    lng: -96.5136,
    keywords: [
      "security guard services Beausejour",
      "mobile patrol Beausejour",
      "commercial security Beausejour",
    ],
    intro:
      "Beausejour anchors the Eastman region and serves as the service centre for a broad rural catchment. Manitoba Security Services provides mobile patrol and commercial coverage across the town.",
    sections: [
      {
        h: "Eastman regional coverage",
        p: "Our Beausejour-adjacent patrol coverage extends into Lac du Bonnet, Pinawa, Powerview-Pine Falls, and the surrounding cottage country during the summer season.",
      },
    ],
  },
  {
    slug: "stonewall",
    name: "Stonewall",
    region: "Interlake",
    tier: "secondary",
    lat: 50.1339,
    lng: -97.3239,
    keywords: [
      "security guard services Stonewall",
      "mobile patrol Stonewall",
      "commercial security Stonewall",
    ],
    intro:
      "Stonewall sits within easy reach of Winnipeg's northwestern edge and anchors a growing commuter and industrial community. Manitoba Security Services provides regular mobile patrol and commercial coverage.",
    sections: [
      {
        h: "Interlake commuter belt coverage",
        p: "Our patrols cover Stonewall's industrial and commercial footprint plus adjacent communities including Warren, Balmoral, and Teulon.",
      },
    ],
  },
  {
    slug: "swan-river",
    name: "Swan River",
    region: "Parkland",
    tier: "secondary",
    lat: 52.1064,
    lng: -101.2681,
    keywords: [
      "security guard services Swan River",
      "forestry security Swan River",
      "industrial security Swan River",
    ],
    intro:
      "Swan River anchors the northern Parkland region and hosts substantial forestry and agri-processing activity. Manitoba Security Services provides industrial and commercial coverage across the town.",
    sections: [
      {
        h: "Forestry and agri-service coverage",
        p: "Our teams cover Swan River's processing plants, transport terminals, and downtown commercial footprint with mobile patrol and static coverage as required.",
      },
    ],
  },
  {
    slug: "altona",
    name: "Altona",
    region: "Pembina Valley",
    tier: "secondary",
    lat: 49.1039,
    lng: -97.5578,
    keywords: [
      "security guard services Altona",
      "commercial security Altona",
      "industrial security Altona",
    ],
    intro:
      "Altona is a significant agri-processing centre in the southern Pembina Valley. Manitoba Security Services provides mobile patrol and commercial coverage across the town.",
    sections: [
      {
        h: "Processing and commercial coverage",
        p: "Our patrols cover Altona's processing footprint, downtown retail, and the surrounding rural service area.",
      },
    ],
  },
  {
    slug: "carman",
    name: "Carman",
    region: "Central Plains",
    tier: "secondary",
    lat: 49.5017,
    lng: -98.0011,
    keywords: [
      "security guard services Carman",
      "commercial security Carman",
      "mobile patrol Carman",
    ],
    intro:
      "Carman is a Central Plains service centre with a stable agri-industrial base. Manitoba Security Services provides mobile patrol and commercial coverage.",
    sections: [
      {
        h: "Central Plains regional coverage",
        p: "Our teams extend from Carman across the surrounding communities including Miami, Elm Creek, and Roland.",
      },
    ],
  },
  {
    slug: "virden",
    name: "Virden",
    region: "Westman",
    tier: "secondary",
    lat: 49.8506,
    lng: -100.9294,
    keywords: [
      "security guard services Virden",
      "oil and gas security Virden",
      "industrial security Virden",
      "mobile patrol Virden",
    ],
    intro:
      "Virden sits in the heart of Manitoba's oil and gas country. Manitoba Security Services provides wellhead, right-of-way, and commercial coverage across the town and surrounding operator footprints.",
    sections: [
      {
        h: "Energy-sector coverage",
        p: "Our Virden-based patrols cover active wellheads, gathering facilities, and pipeline right-of-ways across the surrounding rural municipalities. Coverage integrates with operator control-room protocols.",
      },
    ],
  },
  {
    slug: "gimli",
    name: "Gimli",
    region: "Interlake",
    tier: "secondary",
    lat: 50.6339,
    lng: -96.9925,
    keywords: [
      "security guard services Gimli",
      "event security Gimli",
      "marina security Gimli",
      "seasonal security Interlake",
    ],
    intro:
      "Gimli anchors Manitoba's south Lake Winnipeg tourism corridor and hosts the annual Icelandic Festival — Íslendingadagurinn. Manitoba Security Services provides event and seasonal commercial coverage across the town.",
    sections: [
      {
        h: "Seasonal event and marina coverage",
        p: "Our teams cover Gimli's summer event footprint — including the Icelandic Festival — plus year-round commercial and residential coverage. Marina coverage is available on request.",
      },
    ],
  },
  {
    slug: "pinawa",
    name: "Pinawa",
    region: "Eastman",
    tier: "secondary",
    lat: 50.1789,
    lng: -95.8836,
    keywords: [
      "security guard services Pinawa",
      "commercial security Pinawa",
      "mobile patrol Pinawa",
    ],
    intro:
      "Pinawa sits within the Whiteshell region and hosts a distinctive technical and research community. Manitoba Security Services provides mobile patrol and commercial coverage.",
    sections: [
      {
        h: "Whiteshell corridor coverage",
        p: "Our patrols cover Pinawa's commercial footprint and adjacent communities within the Whiteshell — including Lac du Bonnet and Seven Sisters.",
      },
    ],
  },
  {
    slug: "niverville",
    name: "Niverville",
    region: "Southeast",
    tier: "secondary",
    lat: 49.6039,
    lng: -97.0403,
    keywords: [
      "security guard services Niverville",
      "commercial security Niverville",
      "mobile patrol Niverville",
    ],
    intro:
      "Niverville is one of Manitoba's fastest-growing towns, anchoring the southeast commuter corridor into Winnipeg. Manitoba Security Services provides mobile patrol and commercial coverage.",
    sections: [
      {
        h: "Southeast commuter belt coverage",
        p: "Our patrols cover Niverville's expanding commercial footprint and the adjacent communities of Île-des-Chênes, Landmark, and New Bothwell.",
      },
    ],
  },
  {
    slug: "morris",
    name: "Morris",
    region: "Red River",
    tier: "secondary",
    lat: 49.3572,
    lng: -97.3625,
    keywords: [
      "security guard services Morris",
      "event security Morris",
      "commercial security Morris",
    ],
    intro:
      "Morris sits on the Highway 75 corridor south of Winnipeg and hosts the Manitoba Stampede & Exhibition. Manitoba Security Services provides event and commercial coverage across the town.",
    sections: [
      {
        h: "Highway 75 corridor coverage",
        p: "Our teams cover Morris's event footprint, downtown commercial, and the adjacent communities along the Red River corridor.",
      },
    ],
  },
  {
    slug: "killarney",
    name: "Killarney",
    region: "Westman",
    tier: "secondary",
    lat: 49.1808,
    lng: -99.6642,
    keywords: [
      "security guard services Killarney",
      "commercial security Killarney",
      "mobile patrol Killarney",
    ],
    intro:
      "Killarney anchors the Turtle Mountain region and serves as a regional service centre. Manitoba Security Services provides mobile patrol and commercial coverage.",
    sections: [
      {
        h: "Turtle Mountain coverage",
        p: "Our teams cover Killarney's commercial footprint and surrounding communities including Boissevain and Wawanesa.",
      },
    ],
  },
  {
    slug: "boissevain",
    name: "Boissevain",
    region: "Westman",
    tier: "secondary",
    lat: 49.2317,
    lng: -100.0553,
    keywords: [
      "security guard services Boissevain",
      "oil and gas security Boissevain",
      "commercial security Boissevain",
    ],
    intro:
      "Boissevain sits within the Manitoba portion of the Bakken formation and serves as a service centre for surrounding energy operations. Manitoba Security Services provides wellhead, commercial, and mobile patrol coverage.",
    sections: [
      {
        h: "Energy-sector and commercial coverage",
        p: "Our patrols cover Boissevain's downtown, adjacent energy operations, and the surrounding rural service area.",
      },
    ],
  },
  {
    slug: "melita",
    name: "Melita",
    region: "Westman",
    tier: "secondary",
    lat: 49.2683,
    lng: -100.9903,
    keywords: [
      "security guard services Melita",
      "oil and gas security Melita",
      "wellhead security Melita",
    ],
    intro:
      "Melita is at the centre of Manitoba's most active oil and gas play. Manitoba Security Services provides wellhead, right-of-way, and mobile patrol coverage across the surrounding operator footprints.",
    sections: [
      {
        h: "Energy-sector coverage",
        p: "Our Melita-adjacent patrols cover active wellheads, pump stations, and pipeline right-of-ways across the surrounding rural municipalities.",
      },
    ],
  },
  {
    slug: "souris",
    name: "Souris",
    region: "Westman",
    tier: "secondary",
    lat: 49.6217,
    lng: -100.2547,
    keywords: [
      "security guard services Souris",
      "oil and gas security Souris",
      "commercial security Souris",
    ],
    intro:
      "Souris sits within the Westman oil and gas region and serves as a service centre for surrounding operations. Manitoba Security Services provides commercial, wellhead, and mobile patrol coverage.",
    sections: [
      {
        h: "Energy-sector and commercial coverage",
        p: "Our patrols cover Souris's downtown, event footprint, and the surrounding energy operations.",
      },
    ],
  },
  {
    slug: "minnedosa",
    name: "Minnedosa",
    region: "Westman",
    tier: "secondary",
    lat: 50.2439,
    lng: -99.845,
    keywords: [
      "security guard services Minnedosa",
      "commercial security Minnedosa",
      "mobile patrol Minnedosa",
    ],
    intro:
      "Minnedosa is a Westman service centre with a stable agricultural and light-industrial economy. Manitoba Security Services provides mobile patrol and commercial coverage.",
    sections: [
      {
        h: "Westman regional coverage",
        p: "Our teams cover Minnedosa's downtown and surrounding communities including Neepawa, Rivers, and Erickson.",
      },
    ],
  },
  {
    slug: "russell",
    name: "Russell",
    region: "Parkland",
    tier: "secondary",
    lat: 50.7803,
    lng: -101.2661,
    keywords: [
      "security guard services Russell",
      "commercial security Russell",
      "mobile patrol Russell",
    ],
    intro:
      "Russell anchors the western Parkland region near the Saskatchewan border. Manitoba Security Services provides mobile patrol and commercial coverage.",
    sections: [
      {
        h: "Western Parkland coverage",
        p: "Our teams cover Russell's commercial footprint and surrounding communities including Roblin and Binscarth.",
      },
    ],
  },
  {
    slug: "roblin",
    name: "Roblin",
    region: "Parkland",
    tier: "secondary",
    lat: 51.2364,
    lng: -101.3567,
    keywords: [
      "security guard services Roblin",
      "commercial security Roblin",
      "mobile patrol Roblin",
    ],
    intro:
      "Roblin serves the northwestern Parkland region and adjacent Saskatchewan communities. Manitoba Security Services provides mobile patrol and commercial coverage.",
    sections: [
      {
        h: "Northwestern Parkland coverage",
        p: "Our teams cover Roblin's commercial footprint and the surrounding rural service area.",
      },
    ],
  },
  {
    slug: "ste-anne",
    name: "Ste. Anne",
    region: "Southeast",
    tier: "secondary",
    lat: 49.6664,
    lng: -96.6489,
    keywords: [
      "security guard services Ste. Anne",
      "commercial security Ste. Anne",
      "mobile patrol Ste. Anne",
    ],
    intro:
      "Ste. Anne sits on the Trans-Canada corridor east of Winnipeg and anchors a growing commuter and light-industrial community. Manitoba Security Services provides mobile patrol and commercial coverage.",
    sections: [
      {
        h: "Trans-Canada corridor coverage",
        p: "Our patrols cover Ste. Anne's commercial footprint and adjacent communities including Richer and La Broquerie.",
      },
    ],
  },
  {
    slug: "lac-du-bonnet",
    name: "Lac du Bonnet",
    region: "Eastman",
    tier: "secondary",
    lat: 50.2586,
    lng: -96.0603,
    keywords: [
      "security guard services Lac du Bonnet",
      "cottage country security",
      "commercial security Lac du Bonnet",
    ],
    intro:
      "Lac du Bonnet is the gateway to Manitoba's Whiteshell cottage country. Manitoba Security Services provides mobile patrol, seasonal residential coverage, and commercial protection across the town and surrounding lakes.",
    sections: [
      {
        h: "Whiteshell coverage",
        p: "Our teams cover Lac du Bonnet and adjacent cottage communities — including Pointe du Bois and Seven Sisters — with a scheduled interior/exterior verification service for absentee owners during shoulder seasons.",
      },
    ],
  },
  {
    slug: "powerview-pine-falls",
    name: "Powerview-Pine Falls",
    region: "Eastman",
    tier: "secondary",
    lat: 50.5344,
    lng: -96.2489,
    keywords: [
      "security guard services Powerview-Pine Falls",
      "industrial security Pine Falls",
      "mobile patrol Powerview",
    ],
    intro:
      "Powerview-Pine Falls sits on the Winnipeg River and hosts substantial hydroelectric and industrial infrastructure. Manitoba Security Services provides commercial and industrial coverage.",
    sections: [
      {
        h: "Winnipeg River corridor coverage",
        p: "Our teams cover Powerview-Pine Falls's commercial footprint and surrounding industrial operations.",
      },
    ],
  },
  {
    slug: "snow-lake",
    name: "Snow Lake",
    region: "Northern",
    tier: "secondary",
    lat: 54.879,
    lng: -100.0233,
    keywords: [
      "security guard services Snow Lake",
      "mining security Snow Lake",
      "remote site security Snow Lake",
    ],
    intro:
      "Snow Lake is a northern Manitoba mining community with active gold and base-metal operations. Manitoba Security Services provides mining, industrial, and commercial coverage.",
    sections: [
      {
        h: "Mining and community coverage",
        p: "Our teams cover Snow Lake's mining operations, contractor camps, and community commercial footprint with mining-experienced officers.",
      },
    ],
  },
  {
    slug: "leaf-rapids",
    name: "Leaf Rapids",
    region: "Northern",
    tier: "secondary",
    lat: 56.4506,
    lng: -99.9847,
    keywords: [
      "security guard services Leaf Rapids",
      "remote site security Leaf Rapids",
      "mining security Leaf Rapids",
    ],
    intro:
      "Leaf Rapids is a remote northern Manitoba community with historical and contemporary mining exposure. Manitoba Security Services provides remote-site and community coverage on a scheduled basis.",
    sections: [
      {
        h: "Remote northern coverage",
        p: "Our operations dispatch officers to Leaf Rapids on scheduled and event-driven rotations aligned to client operational cycles.",
      },
    ],
  },
  {
    slug: "lynn-lake",
    name: "Lynn Lake",
    region: "Northern",
    tier: "secondary",
    lat: 56.85,
    lng: -101.0667,
    keywords: [
      "security guard services Lynn Lake",
      "mining security Lynn Lake",
      "remote site security Lynn Lake",
    ],
    intro:
      "Lynn Lake sits at the northwestern edge of Manitoba's mineral belt and supports active exploration and production activity. Manitoba Security Services provides mining and remote-site coverage.",
    sections: [
      {
        h: "Remote mining coverage",
        p: "Our Lynn Lake operations focus on mining site security, exploration-camp posture, and remote logistics support.",
      },
    ],
  },
  {
    slug: "churchill",
    name: "Churchill",
    region: "Northern",
    tier: "secondary",
    lat: 58.7684,
    lng: -94.1650,
    keywords: [
      "security guard services Churchill",
      "port security Churchill",
      "remote site security Churchill",
    ],
    intro:
      "Churchill is Canada's only deep-water Arctic port and a globally significant tourism destination. Manitoba Security Services provides port-adjacent, industrial, and event coverage on a scheduled basis.",
    sections: [
      {
        h: "Port and tourism coverage",
        p: "Our Churchill operations align with rail and port cycles plus the community's polar bear and beluga tourism seasons.",
      },
    ],
  },
  {
    slug: "grand-rapids",
    name: "Grand Rapids",
    region: "Northern",
    tier: "secondary",
    lat: 53.1682,
    lng: -99.2717,
    keywords: [
      "security guard services Grand Rapids",
      "industrial security Grand Rapids",
      "mobile patrol Grand Rapids",
    ],
    intro:
      "Grand Rapids sits at the north end of Lake Winnipeg and hosts significant hydroelectric infrastructure. Manitoba Security Services provides commercial and industrial coverage.",
    sections: [
      {
        h: "North Lake Winnipeg coverage",
        p: "Our teams cover Grand Rapids's commercial footprint and adjacent industrial operations.",
      },
    ],
  },
  {
    slug: "gillam",
    name: "Gillam",
    region: "Northern",
    tier: "secondary",
    lat: 56.3467,
    lng: -94.7100,
    keywords: [
      "security guard services Gillam",
      "industrial security Gillam",
      "remote site security Gillam",
    ],
    intro:
      "Gillam anchors a major hydroelectric corridor in northern Manitoba. Manitoba Security Services provides industrial and remote-site coverage on a scheduled basis.",
    sections: [
      {
        h: "Nelson River coverage",
        p: "Our Gillam operations align with the operational cycles of the surrounding hydroelectric footprint.",
      },
    ],
  },
  {
    slug: "winnipeg-beach",
    name: "Winnipeg Beach",
    region: "Interlake",
    tier: "secondary",
    lat: 50.5089,
    lng: -96.9583,
    keywords: [
      "security guard services Winnipeg Beach",
      "seasonal security Interlake",
      "event security Winnipeg Beach",
    ],
    intro:
      "Winnipeg Beach is a Lake Winnipeg summer community with substantial seasonal population growth. Manitoba Security Services provides event and seasonal residential coverage.",
    sections: [
      {
        h: "Interlake seasonal coverage",
        p: "Our teams cover Winnipeg Beach's event footprint plus scheduled seasonal residential verification.",
      },
    ],
  },
  {
    slug: "teulon",
    name: "Teulon",
    region: "Interlake",
    tier: "secondary",
    lat: 50.3833,
    lng: -97.2667,
    keywords: [
      "security guard services Teulon",
      "commercial security Teulon",
      "mobile patrol Teulon",
    ],
    intro:
      "Teulon sits within the Interlake commuter belt north of Winnipeg. Manitoba Security Services provides mobile patrol and commercial coverage.",
    sections: [
      {
        h: "Interlake corridor coverage",
        p: "Our patrols cover Teulon's commercial footprint and adjacent Interlake communities.",
      },
    ],
  },
];

export const cityBySlug = (slug) => cities.find((c) => c.slug === slug);
export const primaryCities = cities.filter((c) => c.tier === "primary");
